import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";

const DocuSignCallback = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const [status, setStatus] = useState<
    "idle" | "exchanging" | "success" | "failed"
  >("idle");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (error) {
      setStatus("failed");
      setMessage(error);
      return;
    }
    if (!code) return;

    setStatus("exchanging");
    supabase.functions
      .invoke("docusign-oauth-callback", {
        body: { code, state },
      })
      .then(({ error: invokeError }) => {
        if (invokeError) throw invokeError;
        setStatus("success");
        setMessage(
          t(
            "docusignCallback.authorized",
            "DocuSign authorized. Refresh token stored."
          )
        );
      })
      .catch((e: any) => {
        setStatus("failed");
        setMessage(
          e?.message ||
            t(
              "docusignCallback.exchangeFailed",
              "Failed to exchange authorization code."
            )
        );
      });
    // `t` is intentionally omitted: this is a one-time OAuth code exchange
    // gated on `code`/`state`/`error`, and `useLanguage()`'s `t` is a fresh
    // reference on every LanguageProvider render (including the async
    // translations refetch shortly after mount) — listing it here would
    // replay the exchange against an already-consumed DocuSign code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state, error]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        {status === "exchanging" ? (
          <>
            <h1 className="mb-2 text-xl font-semibold">
              {t(
                "docusignCallback.finalizing",
                "Finalizing DocuSign authorization…"
              )}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {t(
                "docusignCallback.exchangingHint",
                "Exchanging the authorization code and storing your refresh token."
              )}
            </p>
          </>
        ) : status === "failed" ? (
          <>
            <h1 className="mb-2 text-xl font-semibold text-destructive">
              {t(
                "docusignCallback.failedTitle",
                "DocuSign authorization failed"
              )}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {message || error}
            </p>
          </>
        ) : status === "success" ? (
          <>
            <h1 className="mb-2 text-xl font-semibold text-green-600">
              {t("docusignCallback.successTitle", "Authorization successful")}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {message ||
                t(
                  "docusignCallback.successHint",
                  "DocuSign has redirected you here. You can return to the admin panel."
                )}
            </p>
          </>
        ) : code ? (
          <>
            <h1 className="mb-2 text-xl font-semibold text-green-600">
              {t("docusignCallback.successTitle", "Authorization successful")}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {t(
                "docusignCallback.finalizingShort",
                "DocuSign has redirected you here. Finalizing…"
              )}
            </p>
          </>
        ) : (
          <>
            <h1 className="mb-2 text-xl font-semibold">
              {t("docusignCallback.callbackTitle", "DocuSign callback")}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {t(
                "docusignCallback.callbackHint",
                "This page is used as the redirect target after DocuSign authorization."
              )}
            </p>
          </>
        )}
        <Link
          to="/admin"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t("docusignCallback.backToAdmin", "Back to Admin")}
        </Link>
      </div>
    </div>
  );
};

export default DocuSignCallback;
